import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Store, MapPin, ArrowRight, UserCheck, UserX } from "lucide-react";
import { formatINR } from "@/lib/format";

export default function HQFranchises() {
  const [hubs, setHubs] = useState([]);
  const [owners, setOwners] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hubs");
      setHubs(data);
    })();
  }, []);

  // We don't have a direct users list endpoint for HQ. Use franchise/dashboard per hub to fetch owner info lazily.
  const loadOwner = async (hubId) => {
    if (owners[hubId]) return;
    try {
      const { data } = await api.get(`/franchise/dashboard?hub_id=${hubId}`);
      setOwners((o) => ({ ...o, [hubId]: data }));
    } catch {}
  };

  useEffect(() => {
    hubs.forEach((h) => loadOwner(h.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubs]);

  return (
    <div className="space-y-8" data-testid="hq-franchises-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
          Network
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          Franchises.
        </h1>
        <p className="text-zinc-400 mt-2">
          Every live hub across India. Open any to view its franchise
          dashboard, staff and royalty statement.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {hubs.map((h) => {
          const data = owners[h.id];
          const owner = data?.owner;
          return (
            <button
              key={h.id}
              onClick={() => nav(`/app/franchise?hub_id=${h.id}`)}
              data-testid={`hq-franchise-card-${h.city.toLowerCase()}`}
              className="text-left rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.04] hover:border-[#D4B878]/40 transition"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Store size={14} className="text-[#E8D6A3]" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#E8D6A3]">
                      {h.format}
                    </span>
                  </div>
                  <div className="text-xl font-bold">{h.name}</div>
                  <div className="text-sm text-zinc-500 flex items-center gap-1 mt-1">
                    <MapPin size={12} /> {h.city}, {h.state}
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={
                    owner
                      ? "border-emerald-500/40 text-emerald-400"
                      : "border-white/10 text-zinc-400"
                  }
                >
                  {owner ? (
                    <>
                      <UserCheck size={10} className="mr-1" /> Assigned
                    </>
                  ) : (
                    <>
                      <UserX size={10} className="mr-1" /> Unassigned
                    </>
                  )}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-white/5">
                <Stat
                  label="Today"
                  value={data ? formatINR(data.today_revenue) : "—"}
                />
                <Stat
                  label="Active"
                  value={data ? data.active_now : "—"}
                />
                <Stat
                  label="Seats"
                  value={data ? `${data.total_seats}` : "—"}
                />
              </div>

              {owner && (
                <div className="mt-4 text-xs text-zinc-500 truncate">
                  Owner · <span className="text-zinc-300">{owner.name}</span>{" "}
                  ({owner.email})
                </div>
              )}
              <div className="mt-4 text-sm text-[#E8D6A3] font-semibold inline-flex items-center gap-1">
                Open dashboard <ArrowRight size={14} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-sm font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </div>
    </div>
  );
}
